<script setup lang="ts">
// UJ-1: name, the data's source (customer, source environment), backup; Peaksolution (hosts, build, optional htaccess); cashdesk,
// ticketservice and contingentservice (hosts, and when enabled a build of their own) → POST /environments, in the CRD's shape (plan.md M7).
// The service host names follow the Peaksolution host (shop-x… → cashdesk-x…) until someone edits them.
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ApiProblem, must } from '../api/client';
import type { Backup, Build, CreateEnvironment } from '../api/types';
import { useContext } from '../context';
import { useSession } from '../stores/session';
import { validate, errorsAt, type FieldError } from '../api/validate';
import { buildHtaccess } from '../forms/htaccess';
import { clusterDomain, defaultShopHost, parseHostList, suggestServiceHosts } from '../forms/hosts';
import { buildLabel } from '../status/status';
import { missingBuild } from '../forms/build';
import BuildPicker from '../components/BuildPicker.vue';
import ProblemAlert from '../components/ProblemAlert.vue';
import Icon from '../components/Icon.vue';

const { api } = useContext();
const session = useSession();
const router = useRouter();

const name = ref('');
const installationId = ref('');
const customer = ref('');
const purpose = ref('');
const backupId = ref('latest');
const backups = ref<Backup[]>([]);
const backupsError = ref<unknown>();
const backupsLoading = ref(false);
const build = ref<Build>();
const shopHosts = ref('');
// until edited by hand the shop host follows the name: shop-<name>.<the cluster's domain>
const domain = clusterDomain(window.location.hostname);
const shopEdited = ref(false);
const shopDefault = computed(() => defaultShopHost(name.value, domain));
watch(shopDefault, (d) => { if (!shopEdited.value) shopHosts.value = d; });
function editShopHosts(value: string) {
  shopHosts.value = value;
  shopEdited.value = value.trim() !== '';
}
function useShopDefault() {
  shopEdited.value = false;
  shopHosts.value = shopDefault.value;
}
const hostExample = (prefix: string) => `${prefix}-<name>.${domain ?? 'stage.peaksolution.com'}`;
// the hosts are asked for even when a service does not run: the wire layer names them (CONFIGURATION_HOST_CASHDESK_B2B, …)
type ServiceForm = { run: boolean; hosts: string; edited: boolean; theme: string; build?: Build };
const cashdesk = ref<ServiceForm>({ run: false, hosts: '', edited: false, theme: '' });
const ticketservice = ref<ServiceForm>({ run: false, hosts: '', edited: false, theme: '' });
const contingentservice = ref<ServiceForm>({ run: false, hosts: '', edited: false, theme: '' });
// wired: the database is wired to its host names, so they are asked for even when it does not run.
// contingentservice is reached by the shop inside the namespace: its hosts are optional and it has no theme
const SERVICES = [
  { key: 'cashdesk', label: 'Cashdesk', prefix: 'cashdesk', num: 4, form: cashdesk, wired: true },
  { key: 'ticketservice', label: 'Ticket service', prefix: 'ticketservice', num: 5, form: ticketservice, wired: true },
  { key: 'contingentservice', label: 'Contingent service', prefix: 'contingentservice', num: 6, form: contingentservice, wired: false },
] as const;
// until a service's hosts are edited by hand they follow the Peaksolution hosts
watch(
  shopHosts,
  (text) => SERVICES.forEach((s) => { if (s.wired && !s.form.value.edited) s.form.value.hosts = suggestServiceHosts(text, s.prefix); }),
  { immediate: true },
);
function editServiceHosts(f: ServiceForm, value: string) {
  f.hosts = value;
  f.edited = true;
}
function useSuggestion(f: ServiceForm, prefix: string) {
  f.edited = false;
  f.hosts = suggestServiceHosts(shopHosts.value, prefix);
}
const shopHostsParsed = computed(() => parseHostList(shopHosts.value));
/** The text holds a URL (or upper case, a port, …): show what is saved. */
const shopNormalised = computed(() => shopHosts.value.split(/[\s,]+/).filter(Boolean).join(' ') !== shopHostsParsed.value.join(' '));
const mitmproxy = ref(false);
const fluentbit = ref(false);
const withHtaccess = ref(false);
const jamesText = ref('');
const extraRules = ref('');

const submitted = ref(false);
const submitting = ref(false);
const summary = ref<HTMLElement>();
const serverError = ref<unknown>();
// One key per wizard visit: a retried submit places no second backup order.
const idempotencyKey = crypto.randomUUID();

onMounted(async () => {
  if (!session.me) await session.load().catch((e) => (serverError.value = e));
  if (session.customers.length === 1) customer.value = session.customers[0]!;
});

const purposes = computed(() => (customer.value ? session.environments(customer.value) : []));
watch(customer, () => {
  purpose.value = purposes.value.length === 1 ? purposes.value[0]! : '';
});
watch([customer, purpose], async ([c, p]) => {
  backups.value = [];
  backupId.value = 'latest';
  backupsError.value = undefined;
  if (!c || !p) return;
  try {
    backupsLoading.value = true;
    backups.value = must(await api.GET('/sources/{customer}/{environment}/backups', { params: { path: { customer: c, environment: p }, query: { limit: 50 } } })).items;
  } catch (e) {
    backupsError.value = e;
  } finally {
    backupsLoading.value = false;
  }
});

const htaccess = computed(() => (withHtaccess.value ? buildHtaccess(jamesText.value, extraRules.value) : { value: undefined, errors: [] }));

function service(c: ServiceForm) {
  const out: NonNullable<CreateEnvironment['cashdesk']> = {};
  const hosts = parseHostList(c.hosts);
  if (hosts.length) out.hosts = hosts;
  if (c.theme.trim()) out.theme = c.theme.trim();
  if (c.run && c.build) out.build = c.build.base;
  return Object.keys(out).length ? out : undefined;
}

const body = computed<CreateEnvironment>(() => {
  const b: CreateEnvironment = {
    name: name.value.trim(),
    source: { customer: customer.value, environment: purpose.value },
    backupId: backupId.value || 'latest',
    peaksolution: { hosts: shopHostsParsed.value, build: build.value as Build },
  };
  if (htaccess.value.value) b.peaksolution.htaccess = htaccess.value.value;
  const cd = service(cashdesk.value);
  const ts = service(ticketservice.value);
  const cs = contingentservice.value.run ? service(contingentservice.value) : undefined;
  if (cd) b.cashdesk = cd;
  if (ts) b.ticketservice = ts;
  if (cs) b.contingentservice = cs;
  if (mitmproxy.value) b.mitmproxy = true;
  if (fluentbit.value) b.fluentbit = true;
  const inst = installationId.value.trim().toUpperCase();
  if (inst) b.installationId = inst;
  return b;
});

/** A service's hosts are needed even when it does not run; its build only when it should. */
function serviceErrors(key: 'cashdesk' | 'ticketservice' | 'contingentservice', c: ServiceForm, wired = true): FieldError[] {
  const out: FieldError[] = [];
  if (wired && !parseHostList(c.hosts).length) out.push({ path: `${key}.hosts`, message: 'needs at least one host name (the wire layer names it, even when the service is not enabled)' });
  if (c.run && !c.build) out.push({ path: `${key}.build`, message: 'is required when it is enabled' });
  return out;
}

const errors = computed<FieldError[]>(() => [
  ...validate(body.value, 'CreateEnvironment'),
  ...(build.value ? [] : missingBuild(build.value, 'peaksolution.build')),
  ...serviceErrors('cashdesk', cashdesk.value),
  ...serviceErrors('ticketservice', ticketservice.value),
  ...serviceErrors('contingentservice', contingentservice.value, false),
  ...(customer.value && !session.customers.includes(customer.value) ? [{ path: 'source.customer', message: 'is not one you are entitled to' }] : []),
]);
const shown = computed(() => (submitted.value ? errors.value : []));
const at = (p: string) => errorsAt(shown.value, p);

async function submit() {
  submitted.value = true;
  serverError.value = undefined;
  if (errors.value.length || htaccess.value.errors.length) {
    await nextTick();
    summary.value?.focus();
    return;
  }
  submitting.value = true;
  try {
    const env = must(await api.POST('/environments', { body: body.value, params: { header: { 'Idempotency-Key': idempotencyKey } } }));
    await router.push({ name: 'detail', params: { name: env.name } });
  } catch (e) {
    serverError.value = e;
  } finally {
    submitting.value = false;
  }
}

function size(n: number): string {
  return n > 1e9 ? `${(n / 1e9).toFixed(1)} GB` : `${(n / 1e6).toFixed(0)} MB`;
}
const problemCode = computed(() => (serverError.value instanceof ApiProblem ? serverError.value.code : undefined));
</script>

<template>
  <nav aria-label="Breadcrumb" class="crumbs"><RouterLink to="/">Environments</RouterLink> <span aria-hidden="true">/</span> New</nav>
  <div>
    <h1>New environment</h1>
    <p class="muted">Restore a customer backup and run a Peaksolution build on it. You become its owner and can share it afterwards.</p>
  </div>
  <p v-if="session.me && !session.customers.length" class="notice">You are not entitled to any customer's data, so you cannot create an environment.</p>

  <div class="cols">
    <form class="main-col" novalidate @submit.prevent="submit">
      <!-- 1 -->
      <section class="card" aria-labelledby="s-basics">
        <h2 id="s-basics" class="step-title"><span class="step-num">1</span>Basics</h2>
        <p class="step-lead">What the environment is called.</p>
        <div class="grid2">
          <div class="field">
            <label for="f-name">Name</label>
            <input id="f-name" v-model="name" type="text" autocomplete="off" spellcheck="false" :aria-invalid="at('name').length > 0" aria-describedby="f-name-hint" data-testid="f-name" />
            <p id="f-name-hint" class="hint">Lower-case letters, digits and dashes, starting with a letter, at most 30. Namespace <code>peak-{{ name || '&lt;name&gt;' }}</code>; unique across the cluster.</p>
            <p v-for="e in at('name')" :key="e.message" class="field-error">Name {{ e.message }}</p>
          </div>
          <div class="field">
            <label for="f-installation">Installation id <span class="opt-label">optional</span></label>
            <input id="f-installation" v-model="installationId" type="text" class="mono" autocomplete="off" spellcheck="false" placeholder="SKISUP-22329"
              :aria-invalid="at('installationId').length > 0" aria-describedby="f-installation-hint" data-testid="f-installation" />
            <p id="f-installation-hint" class="hint">The Metis installation it belongs to. Several environments may share one.</p>
            <p v-for="e in at('installationId')" :key="e.message" class="field-error">Installation id {{ e.message }}</p>
          </div>
        </div>
      </section>

      <!-- 2 -->
      <section class="card" aria-labelledby="s-data">
        <h2 id="s-data" class="step-title"><span class="step-num">2</span>Data source</h2>
        <p class="step-lead">Whose data is loaded. It does not decide who can see the environment, and Refresh data can switch it later.</p>
        <div class="grid2">
          <div class="field">
            <label for="f-customer">Customer</label>
            <select id="f-customer" v-model="customer" :aria-invalid="at('source.customer').length > 0" data-testid="f-customer">
              <option value="">— choose —</option>
              <option v-for="c in session.customers" :key="c" :value="c">{{ c }}</option>
            </select>
            <p v-for="e in at('source.customer')" :key="e.message" class="field-error">Customer {{ e.message }}</p>
          </div>
          <div class="field">
            <label for="f-purpose">Source environment</label>
            <select id="f-purpose" v-model="purpose" :disabled="!customer" :aria-invalid="at('source.environment').length > 0" aria-describedby="f-purpose-hint" data-testid="f-purpose">
              <option value="">— choose —</option>
              <option v-for="p in purposes" :key="p" :value="p">{{ p }}</option>
            </select>
            <p id="f-purpose-hint" class="hint">The installation the backup was taken from (live, stage, int); only the ones you are entitled to.</p>
            <p v-for="e in at('source.environment')" :key="e.message" class="field-error">Source environment {{ e.message }}</p>
          </div>
          <div class="field" style="grid-column: 1 / -1">
            <label for="f-backup">Backup</label>
            <select id="f-backup" v-model="backupId" :disabled="!purpose" :aria-busy="backupsLoading" data-testid="f-backup">
              <option value="latest">latest (the newest when the order is placed)</option>
              <option v-for="b in backups" :key="b.backupId" :value="b.backupId">{{ b.takenAt }} · {{ b.flavour }} · {{ size(b.sizeBytes) }}<template v-if="b.mariadbVersion"> · MariaDB {{ b.mariadbVersion }}</template> · {{ b.backupId }}</option>
            </select>
            <ProblemAlert :error="backupsError" context="Backups" />
          </div>
        </div>
      </section>

      <!-- 3 -->
      <section class="card" aria-labelledby="s-peaksolution">
        <h2 id="s-peaksolution" class="step-title"><span class="step-num">3</span>Peaksolution</h2>
        <p class="step-lead">The shop: the host names it answers on and its build.</p>
        <div style="display: flex; flex-direction: column; gap: 1rem">
          <div class="field">
            <label for="f-shop-hosts">Host names</label>
            <textarea id="f-shop-hosts" :value="shopHosts" rows="2" spellcheck="false" :placeholder="hostExample('shop')" :aria-invalid="at('peaksolution.hosts').length > 0" aria-describedby="f-shop-hosts-hint" data-testid="f-shop-hosts" @input="editShopHosts(($event.target as HTMLTextAreaElement).value)"></textarea>
            <p id="f-shop-hosts-hint" class="hint">One per line, a URL or a host name; the first is the primary and the shop link. The Cashdesk and Ticket service host names follow from these.<template v-if="!shopEdited && shopDefault && shopHosts === shopDefault"> It follows the name until you change it.</template></p>
            <p v-if="shopDefault && shopHostsParsed.join(' ') !== shopDefault" class="hint"><button type="button" class="link" style="font-size: 12.5px" data-testid="f-shop-hosts-default" @click="useShopDefault">Use {{ shopDefault }}</button></p>
            <p v-if="shopNormalised && shopHostsParsed.length" class="hint" style="color: var(--ok-fg)">Saved as {{ shopHostsParsed.join(', ') }}</p>
            <p v-for="e in at('peaksolution.hosts')" :key="e.path + e.message" class="field-error">{{ e.path.replace('peaksolution.hosts', 'Host') }} {{ e.message }}</p>
          </div>
          <BuildPicker v-model="build" id-prefix="create" path-prefix="peaksolution.build" legend="Build" :errors="shown" />
          <p class="info banner" style="font-size: 13px"><Icon name="info" /> <span class="grow">No image to pick: the operator runs the mysql image of the backup's MariaDB version and the lowest php image that meets the build's minimum PHP.</span></p>
        </div>
      </section>

      <!-- 4, 5, 6 -->
      <section v-for="svc in SERVICES" :key="svc.key" class="card" :aria-labelledby="`s-${svc.key}`">
        <div class="step-head">
          <h2 :id="`s-${svc.key}`" class="step-title"><span class="step-num">{{ svc.num }}</span>{{ svc.label }}</h2>
          <label class="inline" :for="`f-${svc.key}-run`" style="font-weight: 600">{{ svc.form.value.run ? 'Enabled' : 'Not enabled' }}</label>
          <input :id="`f-${svc.key}-run`" v-model="svc.form.value.run" type="checkbox" class="switch" role="switch" :aria-label="`Enable ${svc.label}`" :data-testid="`f-${svc.key}-run`" />
        </div>
        <p v-if="svc.wired" class="step-lead">When it is not enabled, nothing runs and only its host names are wired into the database. Enabled, it runs from a build of its own.</p>
        <p v-else class="step-lead">When it is not enabled, nothing runs and the shop keeps the operator's default endpoint. Enabled, it runs from a build of its own and the shop calls it inside the environment.</p>
        <div style="display: flex; flex-direction: column; gap: 1rem">
          <div class="field">
            <label :for="`f-${svc.key}-hosts`">Host names <span v-if="!svc.wired" class="opt-label">optional</span></label>
            <input :id="`f-${svc.key}-hosts`" :value="svc.form.value.hosts" type="text" spellcheck="false" :placeholder="hostExample(svc.prefix)" :aria-invalid="at(`${svc.key}.hosts`).length > 0" :aria-describedby="`f-${svc.key}-hosts-hint`" :data-testid="`f-${svc.key}-hosts`" @input="editServiceHosts(svc.form.value, ($event.target as HTMLInputElement).value)" />
            <div :id="`f-${svc.key}-hosts-hint`" class="inline" style="gap: 4px 10px">
              <template v-if="!svc.form.value.edited && svc.form.value.hosts">
                <span class="pill sm tone-progress">suggested</span>
                <span class="hint">from the Peaksolution host: <code>shop-</code> becomes <code>{{ svc.prefix }}-</code></span>
              </template>
              <template v-else-if="svc.wired && svc.form.value.edited && suggestServiceHosts(shopHosts, svc.prefix) && svc.form.value.hosts !== suggestServiceHosts(shopHosts, svc.prefix)">
                <span class="pill sm tone-idle">edited</span>
                <button type="button" class="link" style="font-size: 12.5px" @click="useSuggestion(svc.form.value, svc.prefix)">Use {{ suggestServiceHosts(shopHosts, svc.prefix) }}</button>
              </template>
              <span v-if="svc.wired" class="hint">Needed even when it is not enabled: the database is wired to these names. Separate several with commas.</span>
              <span v-else class="hint">Only to reach it from outside, e.g. its Swagger UI. Separate several with commas.</span>
            </div>
            <p v-for="e in at(`${svc.key}.hosts`)" :key="e.path + e.message" class="field-error">{{ svc.label }} hosts {{ e.message }}</p>
          </div>
          <template v-if="svc.form.value.run">
            <BuildPicker v-model="svc.form.value.build" :id-prefix="`create-${svc.key}`" :project="svc.key" :path-prefix="`${svc.key}.build`" :legend="`${svc.label} build (required)`" :errors="shown" />
            <p v-for="e in at(`${svc.key}.build`).filter((x) => x.path === `${svc.key}.build`)" :key="e.message" class="field-error">{{ svc.label }} build {{ e.message }}</p>
            <div v-if="svc.wired" class="field" style="max-width: 18rem">
              <label :for="`f-${svc.key}-theme`">Theme <span class="opt-label">optional</span></label>
              <input :id="`f-${svc.key}-theme`" v-model="svc.form.value.theme" type="text" placeholder="peaksolution" />
            </div>
          </template>
        </div>
      </section>

      <!-- 7 -->
      <section class="card" aria-labelledby="s-options">
        <h2 id="s-options" class="step-title"><span class="step-num">7</span>Options</h2>
        <p class="step-lead">Extras for debugging and routing. All off by default.</p>
        <div class="grid2">
          <label class="choice"><input v-model="fluentbit" type="checkbox" /><span><strong>fluentbit</strong><br /><span class="hint">Ship the logs to VictoriaLogs.</span></span></label>
          <label class="choice"><input v-model="mitmproxy" type="checkbox" /><span><strong>mitmproxy</strong><br /><span class="hint">Capture outbound HTTP calls of the shop.</span></span></label>
        </div>
        <div style="margin-top: 1rem; border: 1px solid var(--line); border-radius: 10px; padding: 14px 16px; display: flex; flex-direction: column; gap: 1rem">
          <label class="inline" style="font-weight: 600"><input v-model="withHtaccess" type="checkbox" data-testid="f-htaccess" /> htaccess: James redirects or extra rewrite rules</label>
          <p v-if="!withHtaccess" class="hint" style="margin-top: -0.5rem">Without it the shop gets the CORS origins of its host names only.</p>
          <template v-if="withHtaccess">
            <div class="field">
              <label for="f-james">James mapping</label>
              <textarea id="f-james" v-model="jamesText" rows="4" spellcheck="false" placeholder="- https://web-luxembourg.lakeside.peaksolution.com checkout profile&#10;app_aa_ https://web-aa.lakeside.peaksolution.com shop=https://shop-aa.peak.example" :aria-invalid="htaccess.errors.length > 0" aria-describedby="f-james-hint"></textarea>
              <p id="f-james-hint" class="hint">One line per store stem: <code>&lt;stem&gt; &lt;james url&gt; [shop=&lt;url&gt;] [checkout] [guestcard] [profile]</code>; <code>-</code> is the plain language stores. A JSON list of <code>{website, url, …}</code> works too.</p>
              <p v-for="e in htaccess.errors" :key="e" class="field-error">{{ e }}</p>
            </div>
            <div class="field">
              <label for="f-rules">Extra rewrite rules</label>
              <textarea id="f-rules" v-model="extraRules" rows="5" spellcheck="false" placeholder="RewriteRule ^old/(.*)$ /new/$1 [R=301,L]" aria-describedby="f-rules-hint"></textarea>
              <p id="f-rules-hint" class="hint">Rendered before the catch-all, as <code>rewrite-rules.conf</code>. The operator validates the assembled file and refuses it with a sentence if apache would not load it.</p>
            </div>
          </template>
        </div>
      </section>

      <div v-if="shown.length" ref="summary" class="alert" role="alert" tabindex="-1">The form has {{ shown.length }} problem{{ shown.length === 1 ? '' : 's' }}; they are marked above.</div>
      <ProblemAlert :error="serverError" context="The API refused the environment" />
      <p v-if="problemCode === 'EnvironmentExists'" class="hint">Names are unique across the cluster; choose another.</p>
      <p v-if="problemCode === 'BackupRetired' || problemCode === 'BackupNotFound'" class="hint">Choose another backup, or <code>latest</code>.</p>

      <div class="actions-row">
        <button type="submit" class="primary" :disabled="submitting" data-testid="f-submit">{{ submitting ? 'Creating…' : 'Create environment' }}</button>
        <RouterLink to="/" class="button">Cancel</RouterLink>
      </div>
    </form>

    <aside class="side-col" aria-labelledby="sum-h">
      <section class="card">
        <h2 id="sum-h" style="margin-bottom: 0.25rem">Summary</h2>
        <p class="hint" style="margin-bottom: 0.75rem">Updates as you fill in the form.</p>
        <dl class="sumlist">
          <div><dt>Name</dt><dd><strong>{{ name || '—' }}</strong></dd></div>
          <div><dt>Namespace</dt><dd class="mono">peak-{{ name || '&lt;name&gt;' }}</dd></div>
          <div><dt>Installation</dt><dd class="mono">{{ installationId.trim().toUpperCase() || 'none' }}</dd></div>
          <div><dt>Owner</dt><dd>you</dd></div>
          <div><dt>Data source</dt><dd>{{ customer && purpose ? `${customer} / ${purpose}` : '—' }}</dd></div>
          <div><dt>Backup</dt><dd class="mono">{{ backupId }}</dd></div>
          <div><dt>Peaksolution</dt><dd class="mono">{{ build ? buildLabel(build.base) : '—' }}</dd></div>
          <div v-for="svc in SERVICES" :key="svc.key"><dt>{{ svc.label }}</dt><dd>{{ svc.form.value.run ? `enabled · ${svc.form.value.build ? buildLabel(svc.form.value.build.base) : 'build missing'}` : svc.wired ? 'not enabled · hosts only' : 'not enabled' }}</dd></div>
          <div><dt>Images</dt><dd>chosen by the operator</dd></div>
          <div><dt>Options</dt><dd>{{ [fluentbit && 'fluentbit', mitmproxy && 'mitmproxy', withHtaccess && 'htaccess'].filter(Boolean).join(', ') || 'none' }}</dd></div>
        </dl>
        <p class="hint" style="margin-top: 0.75rem">Only you (and admins) see it at first. Share it from its Access tab once it exists.</p>
      </section>
    </aside>
  </div>
</template>
